import { useQueryClient } from '@tanstack/react-query';

import { useMutation, useQuery } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	checkAlreadyLinkedSitesCoord,
	checkSitesCoord,
	getUnlinkedSitesForLink,
	linkSiteToAsset,
	rewriteLinkedSitesCoord,
	rewriteSiteCoordToAsset,
	unlinkSiteFromAsset,
	unlinkSitesFromAsset,
} from '@api/linkAndVerifySite';
import { getRentedAssets } from '@api/manageSite';

import { extractQueryParams } from '@utils/common';
import { optionsFormattedForSites } from '@utils/formatter';
import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IItemsResponse } from '@interfaces/common/response';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ILinkSiteToAssetDTO } from '@interfaces/crowdcell/dto/linkSiteDTO';
import { ISiteDropdown, IVerifySiteCoordinatesResponse } from '@interfaces/crowdcell/linkSite';
import { IResponseError } from '@interfaces/errors/apiErrors';

import { ESnackbarTypes } from '@enums/common';
import { EErrorCodes } from '@enums/errorCodes';

import {
	ACQUIRED_ASSETS,
	ASSET,
	ASSETS,
	ASSETS_RENTED,
	CHANGE_SITE_LINK,
	CHECK_LINKED_SITES_COORD,
	LINK_SITE_TO_ASSET,
	REWRITE_LINKED_SITES_COORDINATES,
	REWRITE_SITE_COORD_AND_RELINK,
	REWRITE_SITE_COORDINATES,
	SITE_PROFILE,
	SITES_DB,
	UNLINK_SITE_FROM_ASSET,
	UNLINK_SITES_FROM_ASSET,
	UNLINKED_SITES_FOR_LINK,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IUseLinkAndVerifySiteParams {
	closeLinkModal?: () => void;
	closeUnlinkConfirm?: () => void;
	toggleUpdateSiteModal?: () => void;
	closeUpdateSiteModal?: () => void;
	toggleInRangeInform?: () => void;
	isLinkAccess?: boolean;
	setIsEditLinkMode?: (isEditLinkMode: boolean) => void;
}

export const useLinkAndVerifySites = (params: IUseLinkAndVerifySiteParams) => {
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();

	const {
		closeUnlinkConfirm,
		closeLinkModal,
		toggleUpdateSiteModal,
		closeUpdateSiteModal,
		toggleInRangeInform,
		setIsEditLinkMode,
		isLinkAccess,
	} = params || {};

	const { mutate: linkSiteHandler } = useMutation<ILinkSiteToAssetDTO>({
		mutationKey: [LINK_SITE_TO_ASSET],
		mutationFn: linkSiteToAsset,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.linkSiteToAssetSuccess,
				type: ESnackbarTypes.success,
			}));

			if (closeLinkModal) closeLinkModal();

			return [
				queryClient.invalidateQueries([SITES_DB]),
				queryClient.invalidateQueries([ASSETS]),
				queryClient.invalidateQueries([ACQUIRED_ASSETS]),
				queryClient.invalidateQueries([SITE_PROFILE]),
				queryClient.invalidateQueries([ASSET]),
			];
		},
		onError: (err: IResponseError) => {
			const isOutOfRange = err.errors.find((item) => item.code === EErrorCodes.linkSiteOutOfRadius);

			if (toggleUpdateSiteModal && !!isOutOfRange) toggleUpdateSiteModal();
			else handleErrorFromMessage(err);
		},
	});

	const { mutate: relinkSiteHandler } = useMutation<ILinkSiteToAssetDTO & { newSiteId: string }>({
		mutationKey: [CHANGE_SITE_LINK],
		mutationFn: (data: ILinkSiteToAssetDTO & { newSiteId: string }) => {
			return checkSitesCoord({ assetDbId: data.assetDbId, siteDbId: data.newSiteId })
				.then(() => unlinkSiteFromAsset(data.siteDbId))
				.then(() => {
					return linkSiteHandler({ assetDbId: data.assetDbId, siteDbId: data.newSiteId });
				})
				.catch((err: IResponseError) => {
					const isOutOfRange = err.errors.find((item) => {
						return item.code === EErrorCodes.linkSiteOutOfRadius;
					});

					if (toggleUpdateSiteModal && !!isOutOfRange && setIsEditLinkMode) {
						setIsEditLinkMode(true);
						toggleUpdateSiteModal();
					} else handleErrorFromMessage(err);
				});
		},
		onSuccess: () => {
			return [
				queryClient.invalidateQueries([ASSET]),
			];
		},

	});

	const { mutate: updateAndRelinkSiteHandler } = useMutation<ILinkSiteToAssetDTO & { newSiteId: string }>({
		mutationKey: [REWRITE_SITE_COORD_AND_RELINK],
		mutationFn: (data: ILinkSiteToAssetDTO & { newSiteId: string }) => {
			return rewriteSiteCoordToAsset({ assetDbId: data.assetDbId, siteDbId: data.newSiteId })
				.then(() => unlinkSiteFromAsset(data.siteDbId))
				.then(() => linkSiteToAsset({ assetDbId: data.assetDbId, siteDbId: data.newSiteId }))
				.then(() => {
					dispatch(setSnackbar({
						message: responseMessages.linkSiteToAssetSuccess,
						type: ESnackbarTypes.success,
					}));
					dispatch(setSnackbar({
						message: responseMessages.updatedSitesCoordinates,
						type: ESnackbarTypes.success,
					}));

					if (closeUpdateSiteModal) closeUpdateSiteModal();
					if (closeLinkModal) closeLinkModal();

					return [
						queryClient.invalidateQueries([SITES_DB]),
						queryClient.invalidateQueries([ASSETS]),
						queryClient.invalidateQueries([ACQUIRED_ASSETS]),
						queryClient.invalidateQueries([SITE_PROFILE]),
						queryClient.invalidateQueries([ASSET]),
					];
				})
				.catch((err) => {
					if (closeUpdateSiteModal) closeUpdateSiteModal();

					handleErrorFromMessage(err);
				});
		},
	});

	const { mutate: updateAndLinkSiteHandler } = useMutation<ILinkSiteToAssetDTO>({
		mutationKey: [REWRITE_SITE_COORDINATES],
		mutationFn: (data: ILinkSiteToAssetDTO) => {
			return rewriteSiteCoordToAsset(data)
				.then(() => linkSiteToAsset(data))
				.then(() => {
					dispatch(setSnackbar({
						message: responseMessages.linkSiteToAssetSuccess,
						type: ESnackbarTypes.success,
					}));
					dispatch(setSnackbar({
						message: responseMessages.updatedSitesCoordinates,
						type: ESnackbarTypes.success,
					}));

					if (closeUpdateSiteModal) closeUpdateSiteModal();
					if (closeLinkModal) closeLinkModal();

					return [
						queryClient.invalidateQueries([SITES_DB]),
						queryClient.invalidateQueries([ASSETS]),
						queryClient.invalidateQueries([ACQUIRED_ASSETS]),
						queryClient.invalidateQueries([SITE_PROFILE]),
						queryClient.invalidateQueries([ASSET]),
					];
				})
				.catch((err) => {
					if (closeUpdateSiteModal) closeUpdateSiteModal();

					handleErrorFromMessage(err);
				});
		},
	});

	const { mutate: updateLinkedSitesHandler } = useMutation<string[]>({
		mutationKey: [REWRITE_LINKED_SITES_COORDINATES],
		mutationFn: rewriteLinkedSitesCoord,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updatedSitesCoordinates,
				type: ESnackbarTypes.success,
			}));

			if (closeUpdateSiteModal) closeUpdateSiteModal();

			return [
				queryClient.invalidateQueries([SITES_DB]),
				queryClient.invalidateQueries([SITE_PROFILE]),
				queryClient.invalidateQueries([ASSET]),
			];
		},
	});

	const {
		mutate: checkLinkedSitesHandler,
		data: inRadiusStatuses,
		isLoading: inRadiusStatusesLoading,
	} = useMutation<string[], IItemsResponse<IVerifySiteCoordinatesResponse>>({
		mutationKey: [CHECK_LINKED_SITES_COORD],
		mutationFn: checkAlreadyLinkedSitesCoord,
		onSuccess: (data) => {
			const isOutOfRange = data.items.some((item) => !item.isInRadius);

			if (isOutOfRange && toggleUpdateSiteModal) {
				toggleUpdateSiteModal();
			} else if (toggleInRangeInform) {
				toggleInRangeInform();
			}
		},
	});

	const { mutate: unlinkSitesHandler } = useMutation<string[]>({
		mutationKey: [UNLINK_SITES_FROM_ASSET],
		mutationFn: unlinkSitesFromAsset,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.unlinkSitesFromAsset,
				type: ESnackbarTypes.success,
			}));

			if (closeUnlinkConfirm) closeUnlinkConfirm();

			return [
				queryClient.invalidateQueries([SITES_DB]),
				queryClient.invalidateQueries([ACQUIRED_ASSETS]),
				queryClient.invalidateQueries([ASSETS]),
				queryClient.invalidateQueries([SITE_PROFILE]),
				queryClient.invalidateQueries([ASSET]),
			];
		},
	});

	const { mutate: unlinkSiteHandler } = useMutation<string>({
		mutationKey: [UNLINK_SITE_FROM_ASSET],
		mutationFn: unlinkSiteFromAsset,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.unlinkSiteFromAsset,
				type: ESnackbarTypes.success,
			}));

			if (closeUnlinkConfirm) closeUnlinkConfirm();

			return [
				queryClient.invalidateQueries([SITES_DB]),
				queryClient.invalidateQueries([ASSETS]),
				queryClient.invalidateQueries([ACQUIRED_ASSETS]),
				queryClient.invalidateQueries([SITE_PROFILE]),
				queryClient.invalidateQueries([ASSET]),
			];
		},
	});

	const {
		data: rentedAssets,
		isLoading: isLoadingRentedAssets,
	} = useQuery({
		queryKey: [ASSETS_RENTED],
		queryFn: getRentedAssets,
		enabled: [isLinkAccess],
		refetchOnWindowFocus: false,
	});

	const {
		data: unlinkedSites,
		isLoading: isLoadingUnlinkedSites,
	} = useQuery<ISiteDropdown[], TSelectOption<string>[]>({
		queryKey: [UNLINKED_SITES_FOR_LINK],
		select: (data) => optionsFormattedForSites(data || []),
		queryFn: extractQueryParams(getUnlinkedSitesForLink),
		enabled: [isLinkAccess],
		refetchOnWindowFocus: false,
	});

	return {
		unlinkedSites,
		isLoadingUnlinkedSites,
		inRadiusStatusesLoading,
		inRadiusStatuses: inRadiusStatuses?.items || [],
		rentedAssets: rentedAssets?.items || [],
		isLoadingRentedAssets,
		checkLinkedSitesHandler,
		relinkSiteHandler,
		updateLinkedSitesHandler,
		updateAndLinkSiteHandler,
		updateAndRelinkSiteHandler,
		linkSiteHandler,
		unlinkSiteHandler,
		unlinkSitesHandler,
	};
};
